import * as React from 'react'
import { redirect } from 'react-router-dom'

import {
  DealDetailsDocument,
  FinanceSalesModuleRoutesVisibilityDocument,
  QuoteDetailDocument,
  QuotesTabsVisibilityDocument,
  SaleInvoicesDetailsPageRouteDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import QuoteDetailPage from 'modules/finance/pages/QuoteDetailPage'
import { buildDealHeaderProps } from 'modules/finance/routes/ResourceHeaders/deal_header'
import { QuoteDetailHeader } from 'modules/finance/routes/ResourceHeaders/quote_detail_header'
import { buildQuoteHeaderProps } from 'modules/finance/routes/ResourceHeaders/quote_header'
import { SaleInvoiceHeader } from 'modules/finance/routes/ResourceHeaders/sale_invoice_header'
import { contactsRoutes } from 'modules/finance/routes/modularNavigation/contacts'

const SalesRootPage = React.lazy(() => import('modules/finance/pages/SalesRootPage'))
const QuoteUpsertPage = React.lazy(() => import('modules/finance/pages/QuoteUpsertPage'))
const QuotesListPage = React.lazy(() => import('modules/finance/pages/QuotesListPage'))
const QuoteRedirectPage = React.lazy(() => import('modules/finance/pages/QuoteRedirectPage'))
const QuoteDetailsPage = React.lazy(() => import('modules/finance/pages/QuoteDetailsPage'))
const QuotePricingPage = React.lazy(() => import('modules/finance/pages/QuotePricingPage'))
const QuotesRootPage = React.lazy(() => import('modules/finance/pages/QuotesRootPage'))
const DealsRootPage = React.lazy(() => import('modules/finance/pages/DealsRootPage'))
const DealsListPage = React.lazy(() => import('modules/finance/pages/DealsListPage'))
const DealDetailsPage = React.lazy(() => import('modules/finance/pages/DealDetailsPage'))
const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const SaleInvoiceUpsertPage = React.lazy(
  () => import('modules/finance/pages/SaleInvoiceUpsertPage')
)
const SaleInvoicesExporterSidePanel = React.lazy(
  () => import('modules/finance/components/Invoices/SaleInvoicesExporterSidePanel')
)
const SaleInvoicesPage = React.lazy(() => import('modules/finance/pages/SaleInvoicesPage'))
const SaleInvoiceDetailPage = React.lazy(
  () => import('modules/finance/pages/SaleInvoiceDetailPage')
)
const SaleInvoiceDetailRootPage = React.lazy(
  () => import('modules/finance/pages/SaleInvoiceDetailRootPage')
)
const SaleInvoiceDetailDocumentsPage = React.lazy(
  () => import('modules/finance/pages/SaleInvoiceDetailDocumentsPage')
)
const DealInvoicesPage = React.lazy(() => import('modules/finance/pages/DealInvoicesPage'))

export const financeSalesRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.finance.sales.path,
      element: <SalesRootPage />,
      handle: {
        module: 'finance-sales',
        moduleName: () => i18n.t('sidebar.finance_sales'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceSalesModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const canSeeFinanceClients =
              data?.finance?._policies?.sidebarSeeFinanceClientsSubLevelItem?.can ?? false
            const canSeeFinanceQuotes =
              data?.finance?._policies?.sidebarSeeFinanceQuotesSubLevelItem?.can ?? false
            const canSeeFinanceSalesInvoices =
              data?.finance?._policies?.financialDocumentsManageSales?.can ?? false
            const canSeeFinanceDeals =
              data?.finance?._policies?.sidebarSeeFinanceDealsSubLevelItem?.can ?? false
            const canSeeFinanceSalesSettings =
              data?.finance?._policies?.settingsSeeSalesSettings?.can ?? false

            return {
              title: i18n.t('sidebar.finance_sales'),
              settings: canSeeFinanceSalesSettings
                ? navigation.settings.finance.sales.fullPath()
                : undefined,
              subNavigation: [
                {
                  id: 'deals',
                  title: i18n.t('finance.modules.sales.deals'),
                  href: navigation.finance.sales.deals.fullPath(),
                  isVisible: canSeeFinanceDeals,
                },
                {
                  id: 'quotes',
                  title: i18n.t('finance.modules.sales.quotes'),
                  href: navigation.finance.sales.quotes.fullPath(),
                  isVisible: canSeeFinanceQuotes,
                },
                {
                  id: 'sales-invoices',
                  title: i18n.t('finance.modules.sales.sales_invoices'),
                  href: navigation.finance.sales.salesInvoices.fullPath(),
                  isVisible: canSeeFinanceSalesInvoices,
                },
                {
                  id: 'contacts',
                  title: i18n.t('finance.modules.sales.clients'),
                  href: navigation.finance.sales.clients.fullPath(),
                  isVisible: canSeeFinanceClients,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.sales.quotes.path,
          element: <QuotesRootPage />, // Only renders <Outlet />
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.sales.quotes') }),
          },
          children: [
            {
              index: true,
              element: <QuotesListPage />,
            },
            {
              path: navigation.finance.sales.quotes.detail.path,
              element: <QuoteDetailPage />,
              handle: {
                resource: true,
                meta: ({ params, watchQuery, session }) =>
                  watchQuery({
                    query: QuoteDetailDocument,
                    fetchPolicy: 'cache-and-network',
                    variables: {
                      id: String(params['quoteId']),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const quote = data?.finance?.quotes[0]

                    return {
                      headerComponent: <QuoteDetailHeader quote={quote} />,
                      title: quote?.quoteNumber || '-',
                    }
                  }),
              },
            },
            {
              path: navigation.finance.sales.quotes.quoteId.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery, session }) =>
                  watchQuery({
                    query: QuotesTabsVisibilityDocument,
                    fetchPolicy: 'cache-and-network',
                    variables: {
                      id: params['quoteId'] ?? '',
                    },
                  }).map(({ data }) => {
                    const quote = data?.finance?.quote
                    const canSeeProjects =
                      !!data?.projectManagement?._policies?.sidebarSeeProjectsTopLevelItem?.can
                    const authorized = !!quote?._policies?.manage?.can
                    const taxBridgeActivations = data?.finance?.taxBridgeActivations ?? []
                    const needsLegalEntityActivation =
                      quote?.legalEntity?.country === 'es' &&
                      !taxBridgeActivations.some(
                        (activation) =>
                          activation.legalEntityId === quote.legalEntity?.id &&
                          activation.activatedAt
                      )

                    if (!quote) return { title: '' }

                    return {
                      title: quote?.quoteNumber || '-',
                      headerComponentProps: buildQuoteHeaderProps(
                        canSeeProjects,
                        session.access.locale,
                        quote,
                        needsLegalEntityActivation
                      ),
                      subNavigation: [
                        {
                          id: 'pricing',
                          title: i18n.t('finance.quotes.tabs.scope_and_pricing'),
                          href: navigation.finance.sales.quotes.quoteId.pricing.fullPath({
                            quoteId: params['quoteId'] ?? '',
                          }),
                          isVisible: authorized,
                        },
                        {
                          id: 'details',
                          title: i18n.t('finance.quotes.tabs.quote_details'),
                          href: navigation.finance.sales.quotes.quoteId.details.fullPath({
                            quoteId: params['quoteId'] ?? '',
                          }),
                          isVisible: authorized,
                        },
                      ],
                    }
                  }),
              },
              children: [
                {
                  index: true,
                  element: <QuoteRedirectPage />,
                },
                {
                  path: navigation.finance.sales.quotes.quoteId.edit.path,
                  element: <QuoteUpsertPage />,
                },
                {
                  path: navigation.finance.sales.quotes.quoteId.details.path,
                  element: <QuoteDetailsPage />,
                },
                {
                  path: navigation.finance.sales.quotes.quoteId.pricing.path,
                  element: <QuotePricingPage />,
                },
              ],
            },
            {
              path: navigation.finance.sales.quotes.new.path,
              element: <QuoteUpsertPage />,
              handle: {
                meta: () => ({ title: i18n.t('finance.quotes.actions.create') }),
              },
            },
          ],
        },
        {
          path: navigation.finance.sales.deals.path,
          element: <DealsRootPage />,
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.sales.deals') }),
          },
          children: [
            {
              index: true,
              element: <DealsListPage />,
            },
            {
              path: navigation.finance.sales.deals.dealId.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) =>
                  watchQuery({
                    query: DealDetailsDocument,
                    variables: {
                      id: String(params['dealId']),
                    },
                  }).map(({ data }) => {
                    const deal = data?.finance.deal
                    const dealName = deal?.name || '-'
                    return {
                      title: dealName,
                      headerComponentProps: buildDealHeaderProps(data),
                      subNavigation: deal
                        ? [
                            {
                              id: 'details',
                              title: i18n.t('finance.deals.tabs.details'),
                              href: navigation.finance.sales.deals.dealId.details.fullPath({
                                dealId: deal.id,
                              }),
                              isVisible: true,
                            },
                            {
                              id: 'quotes',
                              title: i18n.t('finance.deals.tabs.quotes'),
                              href: navigation.finance.sales.deals.dealId.quotes.fullPath({
                                dealId: deal.id,
                              }),
                              isVisible: true,
                            },
                            {
                              id: 'invoices',
                              title: i18n.t('finance.deals.tabs.invoices'),
                              href: navigation.finance.sales.deals.dealId.invoices.fullPath({
                                dealId: deal.id,
                              }),
                              isVisible: true,
                            },
                          ]
                        : undefined,
                    }
                  }),
              },
              children: [
                {
                  index: true,
                  loader: ({ params }) => {
                    return redirect(
                      navigation.finance.sales.deals.dealId.details.fullPath({
                        dealId: String(params.dealId),
                      })
                    )
                  },
                },
                {
                  path: navigation.finance.sales.deals.dealId.details.path,
                  element: <DealDetailsPage />,
                },
                {
                  path: navigation.finance.sales.deals.dealId.quotes.path,
                  children: [
                    {
                      index: true,
                      element: <QuotesListPage />,
                    },
                    {
                      path: navigation.finance.sales.deals.dealId.quotes.quoteId.path,
                      handle: {
                        resource: true,
                        meta: ({ params, watchQuery, session }) =>
                          watchQuery({
                            query: QuotesTabsVisibilityDocument,
                            fetchPolicy: 'cache-and-network',
                            variables: {
                              id: params['quoteId'] ?? '',
                            },
                          }).map(({ data }) => {
                            const quote = data?.finance?.quote
                            const canSeeProjects =
                              !!data?.projectManagement?._policies?.sidebarSeeProjectsTopLevelItem
                                ?.can
                            const authorized = !!quote?._policies?.manage?.can
                            const taxBridgeActivations = data?.finance?.taxBridgeActivations ?? []
                            const needsLegalEntityActivation =
                              quote?.legalEntity?.country === 'es' &&
                              !taxBridgeActivations.some(
                                (activation) =>
                                  activation.legalEntityId === quote.legalEntity?.id &&
                                  activation.activatedAt
                              )

                            if (!quote) return { title: '' }

                            return {
                              title: quote?.quoteNumber || '-',
                              ancestors: [
                                {
                                  id: 'quotes-list',
                                  title: i18n.t('finance.deals.tabs.quotes'),
                                  to: navigation.finance.sales.deals.dealId.quotes.fullPath({
                                    dealId: params['dealId'] ?? '',
                                  }),
                                },
                              ],
                              headerComponentProps: buildQuoteHeaderProps(
                                canSeeProjects,
                                session.access.locale,
                                quote,
                                needsLegalEntityActivation
                              ),
                              subNavigation: [
                                {
                                  id: 'pricing',
                                  title: i18n.t('finance.quotes.tabs.scope_and_pricing'),
                                  href: navigation.finance.sales.deals.dealId.quotes.quoteId.pricing.fullPath(
                                    {
                                      dealId: params['dealId'] ?? '',
                                      quoteId: params['quoteId'] ?? '',
                                    }
                                  ),
                                  isVisible: authorized,
                                },
                                {
                                  id: 'details',
                                  title: i18n.t('finance.quotes.tabs.quote_details'),
                                  href: navigation.finance.sales.deals.dealId.quotes.quoteId.details.fullPath(
                                    {
                                      dealId: params['dealId'] ?? '',
                                      quoteId: params['quoteId'] ?? '',
                                    }
                                  ),
                                  isVisible: authorized,
                                },
                              ],
                            }
                          }),
                      },
                      children: [
                        {
                          index: true,
                          loader: ({ params }) => {
                            return redirect(
                              navigation.finance.sales.deals.dealId.quotes.quoteId.pricing.fullPath(
                                {
                                  dealId: String(params.dealId),
                                  quoteId: String(params.quoteId),
                                }
                              )
                            )
                          },
                        },
                        {
                          path: navigation.finance.sales.deals.dealId.quotes.quoteId.pricing.path,
                          element: <QuotePricingPage />,
                        },
                        {
                          path: navigation.finance.sales.deals.dealId.quotes.quoteId.details.path,
                          element: <QuoteDetailsPage />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: navigation.finance.sales.deals.dealId.invoices.path,
                  element: <DealInvoicesPage />,
                },
              ],
            },
          ],
        },
        ...contactsRoutes(navigation, 'client'),
        {
          path: navigation.finance.sales.salesInvoices.path,
          element: <SaleInvoicesPage />,
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.sales.sales_invoices') }),
          },
          children: [
            {
              path: navigation.finance.sales.salesInvoices.detail.path,
              element: <SaleInvoiceDetailRootPage />,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) =>
                  watchQuery({
                    query: SaleInvoicesDetailsPageRouteDocument,
                    variables: {
                      id: String(params['invoiceId']),
                    },
                  }).map(({ data }) => {
                    const saleInvoice = data?.finance.financialDocument
                    const saleInvoiceSequencePreview =
                      data?.finance.financialDocument?.salesInvoiceSequence?.humanReadablePreview
                    const saleInvoiceTitle =
                      saleInvoice && saleInvoice.documentNumber
                        ? saleInvoice.documentNumber
                        : (saleInvoiceSequencePreview ?? '-')

                    return {
                      title: saleInvoiceTitle,
                      headerComponent: (
                        <SaleInvoiceHeader
                          saleInvoice={saleInvoice}
                          humanReadablePreview={saleInvoiceSequencePreview}
                        />
                      ),
                      subNavigation: saleInvoice
                        ? [
                            {
                              id: 'overview',
                              title: i18n.t('finance.sale_invoices.detail.overview'),
                              href: navigation.finance.sales.salesInvoices.detail.overview.fullPath(
                                {
                                  invoiceId: saleInvoice.id,
                                }
                              ),
                              isVisible: true,
                            },
                            {
                              id: 'documents',
                              title: i18n.t('finance.sale_invoices.detail.documents'),
                              href: navigation.finance.sales.salesInvoices.detail.documents.fullPath(
                                {
                                  invoiceId: saleInvoice.id,
                                }
                              ),
                              isVisible: true,
                            },
                          ]
                        : undefined,
                    }
                  }),
              },
              children: [
                {
                  index: true,
                  loader: ({ params }) => {
                    return redirect(
                      navigation.finance.sales.salesInvoices.detail.overview.fullPath({
                        invoiceId: String(params.invoiceId),
                      })
                    )
                  },
                },
                {
                  path: navigation.finance.sales.salesInvoices.detail.overview.path,
                  element: <SaleInvoiceDetailPage />,
                },
                {
                  path: navigation.finance.sales.salesInvoices.detail.documents.path,
                  element: <SaleInvoiceDetailDocumentsPage />,
                },
                {
                  path: navigation.finance.sales.salesInvoices.detail.form.path,
                  element: (
                    <SaleInvoiceUpsertPage
                      backPage={navigation.finance.sales.salesInvoices.detail.overview.fullPath({
                        invoiceId: '-1',
                      })}
                    />
                  ),
                },
              ],
            },
            {
              path: navigation.finance.sales.salesInvoices.create.path,
              element: (
                <SaleInvoiceUpsertPage
                  backPage={navigation.finance.sales.salesInvoices.fullPath()}
                />
              ),
              handle: {
                meta: () => ({ title: i18n.t('finance.sale_invoices.datatable.create_invoice') }),
              },
            },
            {
              path: navigation.finance.sales.salesInvoices.invoiceId.path,
              element: (
                <SaleInvoiceUpsertPage
                  backPage={navigation.finance.sales.salesInvoices.fullPath()}
                />
              ),
              handle: {
                meta: () => ({ title: i18n.t('finance.sale_invoices.form.edit_invoice') }),
              },
            },
            {
              path: navigation.finance.sales.salesInvoices.exporter.path,
              element: <SaleInvoicesExporterSidePanel />,
            },
          ],
        },
      ],
    }),
  ]
})
